import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Skeleton,
  Tooltip as MuiTooltip,
  Button as MuiButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Speed,
  Warning as WarningIcon2,
  Pause,
  ViewModule as CardViewIcon,
  ViewList as TableViewIcon,
} from '@mui/icons-material';

import { WebsitesTable } from '../components/WebsitesTable';
import { WebsiteCard } from '../../components/WebsiteCard';
import { Website as ManagementWebsite } from '../types';
import { Website as ApiWebsite } from '../../types/website';
import { WebsiteSummary } from '../../types/website';

interface WebsitesTabProps {
  websites: ManagementWebsite[];
  rawWebsites: ApiWebsite[];
  summary: WebsiteSummary;
  loading: boolean;
  onMenuOpen: (
    event: React.MouseEvent<HTMLElement>,
    type: 'website',
    item: ManagementWebsite
  ) => void;
  onAddWebsite: () => void;
  onOpenAlertsDrawer: (website: ManagementWebsite) => void;
  onWebsiteClick: (website: ManagementWebsite) => void;
}

const TableSkeleton = () => (
  <Box>
    {[1, 2, 3, 4, 5].map((i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        height={60}
        sx={{ mb: 1, borderRadius: 1 }}
      />
    ))}
  </Box>
);

export function WebsitesTab({
  websites,
  rawWebsites,
  summary,
  loading,
  onMenuOpen,
  onAddWebsite,
  onOpenAlertsDrawer,
  onWebsiteClick,
}: WebsitesTabProps) {
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');

  if (loading) {
    return <TableSkeleton />;
  }

  return (
    <Box>
      {/* Summary Stats Bar */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: 2.5 },
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'success.main' }}>
            {summary.up}
          </Typography>
          <Typography variant="body2" color="text.secondary">Up</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ErrorIcon sx={{ fontSize: 20, color: summary.down > 0 ? 'error.main' : 'text.disabled' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: summary.down > 0 ? 'error.main' : 'text.disabled' }}>
            {summary.down}
          </Typography>
          <Typography variant="body2" color="text.secondary">Down</Typography>
        </Box>
        {summary.paused > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Pause sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {summary.paused}
              </Typography>
              <Typography variant="body2" color="text.secondary">Paused</Typography>
            </Box>
          </>
        )}
        <Divider orientation="vertical" flexItem />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Speed sx={{ fontSize: 20, color: 'info.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {summary.avg_response_time_ms !== null ? `${summary.avg_response_time_ms}ms` : '--'}
          </Typography>
          <Typography variant="body2" color="text.secondary">Avg</Typography>
        </Box>
        {summary.ssl_expiring_soon > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <MuiTooltip title="SSL certificates expiring within 30 days">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon2 sx={{ fontSize: 20, color: 'warning.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {summary.ssl_expiring_soon}
                </Typography>
                <Typography variant="body2" color="text.secondary">SSL Expiring</Typography>
              </Box>
            </MuiTooltip>
          </>
        )}
        {/* View Toggle */}
        <Box sx={{ ml: 'auto' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="table"><TableViewIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="card"><CardViewIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Table View */}
      {viewMode === 'table' && (
        <WebsitesTable
          websites={websites}
          onMenuOpen={onMenuOpen}
          onAddWebsite={onAddWebsite}
          onOpenAlertsDrawer={onOpenAlertsDrawer}
          onWebsiteClick={onWebsiteClick}
        />
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <MuiButton
              variant="contained"
              size="small"
              onClick={onAddWebsite}
            >
              Add Website
            </MuiButton>
          </Box>
          {websites.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No websites found
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              {rawWebsites
                .filter((w) => websites.some((fw) => fw.id === w.id))
                .map((website) => (
                  <WebsiteCard
                    key={website.id}
                    website={website}
                    onClick={() => {
                      const adapted = websites.find((fw) => fw.id === website.id);
                      if (adapted) {
                        onWebsiteClick(adapted);
                      }
                    }}
                  />
                ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
